import { Building2, ChevronRight, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";

type Org = {
  id: string;
  name: string;
  slug: string;
  capability: "full_marketplace" | "seller_only";
  orgNumber: string | null;
  createdAt: string;
  memberCount: number;
  auctionCount: number;
};

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

export function OrganisasjonerPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch(`${API}/api/admin/organizations`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setOrgs(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = orgs.filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.slug.toLowerCase().includes(search.toLowerCase()) ||
      (o.orgNumber ?? "").includes(search),
  );

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
            Organisasjoner
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {loading ? "Laster..." : `${filtered.length} organisasjoner`}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Søk etter navn, slug eller org.nr..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table — desktop */}
      <Card className="hidden sm:block overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Navn</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tilgang</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Org.nr</th>
              <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Brukere</th>
              <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Auksjoner</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opprettet</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground text-sm">Laster organisasjoner...</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground text-sm">Ingen organisasjoner funnet</td>
              </tr>
            ) : (
              filtered.map((org) => (
                <tr key={org.id} className="hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate(`/organisasjoner/${org.id}`)}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-foreground">{org.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{org.slug}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground text-xs">
                    {org.capability === "seller_only" ? "Kun selger" : "Full markedsplass"}
                  </td>
                  <td className="px-6 py-4 text-muted-foreground font-mono text-xs">{org.orgNumber ?? "—"}</td>
                  <td className="px-6 py-4 text-right text-foreground">{org.memberCount}</td>
                  <td className="px-6 py-4 text-right text-foreground">{org.auctionCount}</td>
                  <td className="px-6 py-4 text-muted-foreground">{formatDate(org.createdAt)}</td>
                  <td className="px-6 py-4">
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <p className="text-center text-sm text-muted-foreground py-8">Laster organisasjoner...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">Ingen organisasjoner funnet</p>
        ) : (
          filtered.map((org) => (
            <Card key={org.id} className="p-4 cursor-pointer" onClick={() => navigate(`/organisasjoner/${org.id}`)}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/8 flex-shrink-0">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">{org.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{org.slug}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {org.capability === "seller_only" ? "Kun selger" : "Full markedsplass"}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-muted-foreground">{org.memberCount} brukere</span>
                      <span className="text-xs text-muted-foreground">{org.auctionCount} auksjoner</span>
                    </div>
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatDate(org.createdAt)}</span>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
